import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import queryString from "query-string";

import { Fab } from "@mui/material";
import { MdAdd, MdEdit } from "react-icons/md";

import Report from "Components/Report";
import { Link, useLocation, useNavigate } from "react-router-dom";

import ls from "Localization";

import { getMarketings, clearValues } from "store/actions/marketing";

import { MarketingStatus } from "../../../Types/Marketing";

import useStyles from "./styles";
import { stringSort } from "helpers/string";

import _ from "lodash";
import dayjs from "dayjs";

let getMarketingsDebounded: (() => any) & _.Cancelable = _.debounce(
	() => {},
	250
);

const ls2: any = ls;

interface TableState {
    page: number;
    pageSize: number;
    sorted: any[]
    filtered: any[];
    toExport: boolean;
    callback: any; 
}

function RenderMarketingsReport() {
	const dispatch = useDispatch<any>();
	const classes = useStyles({});

	const navigate = useNavigate();
	const location = useLocation();

	const query = queryString.parse(location.search);

	const table: React.RefObject<any> = useRef(null);

	useEffect(() => {
		dispatch(clearValues());
	}, [dispatch]);

	const { items, pages, loading } = useSelector<any, any>(s => s.marketing);

	const columns = [
		{
			Header: ls.creationDate,
			id: "creationDate",
			accessor: (c: any) =>
				dayjs(c.creationDate).format(ls.dateFormatWithoutSeconds),
			width: 150,
			show: true,
			sortMethod: (a: any, b: any, desc: boolean) => {
				const aD = dayjs(a, ls.dateFormatWithoutSeconds);
				const bD = dayjs(b, ls.dateFormatWithoutSeconds);

				return aD.isSame(bD) ? 0 : aD.isAfter(bD) ? 1 : -1;
			}
		},
		{
			Header: ls.name,
			accessor: "name",
			sortMethod: stringSort
		},
		{
			Header: ls.startAt,
			id: "startAt",
			accessor: (c: any) =>
				c.startAt ? dayjs(c.startAt).format(ls.dateFormatWithoutSeconds) : "",
			width: 150,
			show: true,
			sortMethod: (a: any, b: any, desc: boolean) => {
				const aD = dayjs(a, ls.dateFormatWithoutSeconds);
				const bD = dayjs(b, ls.dateFormatWithoutSeconds);

				return aD.isSame(bD) ? 0 : aD.isAfter(bD) ? 1 : -1;
			}
		},
		{
			Header: ls.status,
			id: "status",
			accessor: (c: any) => ls2[MarketingStatus[c.status]],
			Filter: ({ filter, onChange }: any) => {
				return (
					<select
						onChange={event => onChange(event.target.value)}
						style={{ width: "100%" }}
						value={filter?.value ?? ""}
					>
						<option value="">Todos</option>
						{Object.keys(MarketingStatus)
							.filter(c => !isNaN(+c))
							.map((c: any) => (
								<option key={c} value={c}>
									{ls2[MarketingStatus[c]]}
								</option>
							))}
					</select>
				);
			},
			width: 200
		},
		{
			Header: ls.actions,
			id: "actions",
			accessor: (c: any) => (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flex: 1
					}}
				>
					<Link to={`/marketing/${c._id}/Edit`}>
						<MdEdit />
					</Link>
				</div>
			),
			filterable: false,
			width: 80
		}
	];

	const handleFetchData = (tableState: TableState, instance: any) => {
		const { page, pageSize, sorted, filtered, toExport, callback } = tableState;

		if (getMarketingsDebounded) {
			getMarketingsDebounded.cancel();
		}
	
		// if (!sorted) {
		// 	sorted = [];
		// }
	
		getMarketingsDebounded = _.debounce(
			() =>
				dispatch(
					getMarketings(
						page * pageSize,
						pageSize,
						filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							""
						),
						sorted[0]?.id,
						sorted[0]?.desc,
						toExport,
						callback
					)
				),
			500
		);
	
		getMarketingsDebounded();
	};

	useEffect(() => {
		if (table.current) {
			const { page, pageSize, sorted, filtered } = table.current.state;

			if (getMarketingsDebounded) {
				getMarketingsDebounded.cancel();
			}

			// if (!sorted) sorted = [];

			getMarketingsDebounded = _.debounce(
				() =>
					dispatch(
						getMarketings(
							page * pageSize,
							pageSize,
							filtered.reduce(
								(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
								""
							),
							sorted[0]?.id,
							sorted[0]?.desc
						)
					),
				500
			);

			getMarketingsDebounded();
		}
	}, [dispatch]);

	return (
		<div>
			<Report
				manual
				titleText=""
				title="Marketing"
				data={items}
				tableRef={table}
				pages={pages}
				onFetchData={handleFetchData}
				filterable
				loading={loading.getAll}
				columns={columns}
				visibleColumns={columns.map(c => c.Header)}
				defaultFilterMethod={(filter, row) =>
					String(row[filter.id])
						.toLowerCase()
						.indexOf(filter.value.toLowerCase()) > -1
				}
				defaultFiltered={[{ id: "status", value: query?.status ?? "" }]}
				onRowClicked={row => navigate("/Dashboard/Marketing/" + row._id)}
			/>
			<Fab
				color="primary"
				aria-label="Add"
				style={{ position: 'fixed', bottom: '16px', right: '16px',  zIndex: 1000}}
				onClick={() => navigate("/Dashboard/Marketing/Create")}
			>
				<MdAdd size={24} />
			</Fab>
		</div>
	);
}

export default RenderMarketingsReport;
